// ConfirmOverlay.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Form, Badge } from "react-bootstrap";
import { useWhitelabel } from "../../contexts/WhitelabelContext";

const normalize = (s) => (s ?? "").trim();

export function ConfirmOverlay({
  title = "Confirm action",
  message,
  variant = "primary",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  requireText,
  requireTextLabel,
  onCancel,
  onConfirm,
  meta,
  value,
  onChange,
  canConfirm,
}) {
  const { backgroundColor, fontColor } = useWhitelabel();
  const isControlled = typeof onChange === "function";
  const [typed, setTyped] = useState(value);

  useEffect(() => {
    if (!isControlled && value != null) {
      setTyped(String(value));
    }
  }, [isControlled, value]);

  const currentValue = isControlled ? value ?? "" : typed;
  const handleChange = useCallback(
    (next) => {
      if (isControlled) onChange(next);
      else setTyped(next);
    },
    [isControlled, onChange]
  );

  const resolvedCanConfirm = useMemo(() => {
    if (typeof canConfirm === "boolean") return canConfirm;
    if (!requireText) return true;
    return normalize(currentValue) === normalize(requireText);
  }, [canConfirm, requireText, currentValue]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && resolvedCanConfirm) {
      e.preventDefault();
      onConfirm?.();
    }
  };

  return (
    <div
      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        background: "rgba(0,0,0,0.45)",
        zIndex: 5,
        padding: 16,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-3 shadow p-3 p-md-4`}
        style={{ maxWidth: 520, width: "100%" }}
      >
        <div className={`h5 mb-2 ${variant === "danger" ? "text-danger" : ""}`}>
          {title}
        </div>
        {message && <p className="mb-3">{message}</p>}

        {meta && (
          <div className="mb-3">
            {Object.entries(meta).map(([k, v]) => (
              <div key={k} className="text-muted small">
                <strong>{k}:</strong> {v}
              </div>
            ))}
          </div>
        )}

        {requireText && (
          <div className="mb-3">
            <Form.Label className="small">
              {requireTextLabel || (
                <>
                  Type{" "}
                  <Badge bg="light" className="text-dark">
                    {requireText}
                  </Badge>{" "}
                  to continue
                </>
              )}
            </Form.Label>
            <Form.Control
              value={currentValue}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={requireText}
              autoFocus
            />
            <div className="small text-muted mt-1">
              This extra check prevents accidental destructive actions.
            </div>
          </div>
        )}

        <div className="d-flex gap-2 justify-content-end">
          <Button variant="outline-secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!resolvedCanConfirm}
            style={{
              "--dynamic-bg-color": backgroundColor,
              "--dynamic-font-color": fontColor,
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
